import { useEffect, useState } from "react";
import { ApiError, apiFetch } from "@dearbill/shared";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export interface PdfBlob {
  blob: Blob;
  /** Object URL of `blob`, for the download fallback; revoked on unmount or refetch. */
  url: string;
}

export interface PdfBlobState {
  doc: PdfBlob | null;
  failed: boolean;
}

/**
 * Fetches a prefilled PDF as an authenticated blob for `DocumentViewer`.
 *
 * The endpoint needs the portal bearer, so the browser can't load it from a
 * URL; pdf.js draws the blob, and only the "Download" fallback needs the
 * object URL created here. A null `path` fetches nothing, so a caller can
 * hold the fetch until its own gate passes. A 401 calls `onSignOut`; any
 * other failure sets `failed`.
 */
export function usePdfBlob(
  path: string | null,
  session: string,
  onSignOut: () => void,
): PdfBlobState {
  const [doc, setDoc] = useState<PdfBlob | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (path === null) return undefined;
    let cancelled = false;
    let createdUrl: string | null = null;
    apiFetch<Blob>(path, {}, { baseUrl: API_BASE, token: session, responseType: "blob" })
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setDoc({ blob, url: createdUrl });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onSignOut();
          return;
        }
        setFailed(true);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [path, session, onSignOut]);

  return { doc, failed };
}
